import type { z } from 'zod/v4';

export type SsrPropsSurface = 'ssr' | 'hydrate';

export type ValidateSsrPropsInput = {
  name: string;
  idx: string | number;
  data: unknown;
  propsSchema: z.ZodType | undefined;
  surface: SsrPropsSurface;
};

type IssueLike = {
  path: PropertyKey[];
  message: string;
};

/**
 * Formats a Zod issue path as a dotted field path for developer-facing messages.
 *
 * @param path - Zod issue path segments (empty means the object root).
 * @returns Human-readable path such as `tags.0.style`, or `(root)`.
 */
function formatIssuePath(path: PropertyKey[]): string {
  if (path.length === 0) {
    return '(root)';
  }

  return path.map(String).join('.');
}

/**
 * Formats Zod issues as one line per field: `field.path: message`.
 *
 * @param issues - Zod issue list from a failed `safeParse`.
 * @returns Multi-line details indented for console / HTML comment output.
 */
export function formatSsrPropsIssues(issues: readonly IssueLike[]): string {
  return issues
    .map((issue) => `${formatIssuePath(issue.path)}: ${issue.message}`)
    .join('\n  ');
}

/**
 * Observational SSR props validation (poc phase).
 * Missing schema → noop. Invalid data → console.warn and return the warning text;
 * caller still uses raw `data` (no strip/coerce).
 *
 * @param input.name - SSR component name for log context.
 * @param input.idx - Component index / id for log context.
 * @param input.data - Raw host payload to validate.
 * @param input.propsSchema - Optional Zod schema exported by the SSR entry.
 * @param input.surface - Whether validation runs on SSR or hydrate (log prefix).
 * @returns Warning message when invalid; `null` when schema is missing or data is valid.
 */
export function validateSsrProps({
  name,
  idx,
  data,
  propsSchema,
  surface,
}: ValidateSsrPropsInput): string | null {
  if (propsSchema == null) {
    return null;
  }

  const parsed = propsSchema.safeParse(data);
  if (parsed.success) {
    return null;
  }

  const prefix = surface === 'ssr' ? '[ssr props]' : '[hydrate props]';
  const details = formatSsrPropsIssues(parsed.error.issues);
  const message = `${prefix} ${name} idx=${idx}: invalid props\n  ${details}`;

  console.warn(message);
  return message;
}
